<template>
  <div class="h-view flex w-full flex-col overflow-auto bg-white">
    <div class="flex grow flex-row items-center justify-center">
      <h2
        v-if="noEvents"
        class="flex max-w-(--breakpoint-xl) flex-col items-center justify-center p-8 text-center text-2xl"
      >
        Programmet har ikke blitt sluppet for UL enda, men sjekk tilbake senere!
      </h2>
      <div v-else class="mx-auto flex w-full max-w-(--breakpoint-xl) flex-col space-y-4 self-center pt-4 pb-12">
        <Toggle v-model="showUlPluss" label="Vis UL PLUSS" class="ml-2" />

        <div class="flex w-full flex-col items-center justify-center">
          <div
            class="bg-ul-orange-150 flex w-full flex-row justify-center shadow-sm duration-200"
            :class="activePostIndex === 0 ? '' : ''"
          >
            <div
              v-for="(day, idx) in days"
              :key="day.name"
              class="first: last: hover:bg-opacity-75 relative flex h-16 w-1/5 cursor-pointer items-center justify-center text-lg font-semibold tracking-wide duration-200 sm:h-24"
              :class="[
                activeDayIndex == idx
                  ? 'bg-ul-orange-300 z-1 text-white shadow-lg'
                  : 'text-ul-orange-400 hover:bg-ul-orange-100',
                activePostIndex === 0 ? 'first: last:' : '',
              ]"
              @click="selectDay(idx)"
            >
              <span class="hidden sm:inline-block">{{ day.name }}</span>
              <span class="sm:hidden">{{ day.name.substring(0, 3) }}</span>
            </div>
          </div>
          <div class="flex w-full flex-col">
            <div
              v-for="(post, idx) in day"
              :key="post.title + post.timestamp + idx"
              class="flex w-full flex-col space-y-4 py-2 shadow-sm duration-200 hover:cursor-pointer"
              :class="[
                idx + 1 === activePostIndex ? '' : '',
                idx === activePostIndex
                  ? 'even:bg-ul-orange-150 odd:bg-ul-orange-200 my-4'
                  : 'even:bg-ul-orange-50 odd:bg-ul-orange-100',
                idx - 1 === activePostIndex ? '' : '',
              ]"
              @click="togglePost(idx)"
            >
              <div class="flex flex-row">
                <div class="flex w-32 grow items-center justify-center">
                  <div class="relative">
                    <img
                      :src="post.image || getProgramPostImage(post.type)"
                      alt=""
                      class="h-16 w-16 object-cover duration-300"
                    />
                    <div class="absolute top-10 left-6 -rotate-6" v-if="post.ulPluss">
                      <p class="bg-ul-orange-400 relative flex items-center justify-center px-2 font-bold text-white">
                        UL+
                      </p>
                    </div>
                  </div>
                </div>
                <div class="text-ul-orange-300 flex w-full flex-col justify-center pr-2">
                  <p class="text-ul-orange-350 max-w-full text-sm tracking-wider uppercase">
                    {{ getTimestampString(post.timestamp) }}
                  </p>
                  <p class="text-ul-orange-400 max-w-full font-bold">{{ post.title }}</p>
                  <p class="text-ul-orange-600 max-w-full">{{ post.location }}</p>
                </div>
              </div>
              <div
                class="flex w-full items-center px-4 text-left"
                :class="idx === activePostIndex && post.description ? '' : 'hidden'"
              >
                <p class="v-html text-black" v-html="post.description" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <PageFooter class="shrink-0" />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { computed } from 'vue'
import { onMounted } from 'vue'
import { watch } from 'vue'

import activites from '../assets/program/Aktiviteter.png'
import other from '../assets/program/Annet.png'
import sermon from '../assets/program/Møte.png'
import seminar from '../assets/program/Seminar.png'
import entertainment from '../assets/program/Underholdning.png'
import PageFooter from '../components/PageFooter.vue'
import Toggle from '../components/Toggle.vue'
import { useProgramEvents } from '../lib/useProgramEvents'
import { useEventStore } from '../stores/events'
// eslint-disable-next-line no-redeclare
import type { Event } from '../types'

const days = [{ name: 'Tirsdag' }, { name: 'Onsdag' }, { name: 'Torsdag' }, { name: 'Fredag' }, { name: 'Lørdag' }]

const showUlPluss = ref(false)
const activeDayIndex = ref(0)
const activePostIndex = ref<number | null>(null)

const eventStore = useEventStore()
const noEvents = computed(() => eventStore.events.length === 0)
const { eventsByDay } = useProgramEvents()

const day = computed(() =>
  eventsByDay.value[activeDayIndex.value].filter((event) => showUlPluss.value || !event.ulPluss)
)

function selectDay(dayIndex: number) {
  activeDayIndex.value = dayIndex
  activePostIndex.value = null
}
function togglePost(postIndex: number) {
  activePostIndex.value = activePostIndex.value === postIndex ? null : postIndex
}

function getDayString(weekdayIndex: number) {
  return ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'][weekdayIndex]
}
function getTimestampString(timestamp: string) {
  const date = new Date(timestamp)
  return `${getDayString(date.getDay())} ${date.toLocaleTimeString().substring(0, 5)}`
}

function getProgramPostImage(type: string) {
  switch (type) {
    case 'Møte':
      return sermon
    case 'Seminar':
      return seminar
    case 'Aktivitet':
      return activites
    case 'Underholdning':
      return entertainment
    default:
      return other
  }
}

onMounted(() => {
  const hashValue = window.location.hash.replace('#', '')
  const hasLocalStorageItem = localStorage.getItem('program-show-ul-pluss') !== null
  if (hashValue === 'pluss' || hasLocalStorageItem) {
    showUlPluss.value = true
  }
  window.location.hash = ''
})

watch(showUlPluss, (newValue) => {
  if (newValue) {
    localStorage.setItem('program-show-ul-pluss', 'true')
  } else {
    localStorage.removeItem('program-show-ul-pluss')
  }
})
</script>

<style lang="scss"></style>
